import type { EngineProposalDTO, EngineViolationDTO } from '@/engine/canonBundle';
import type { AutowriteStop } from '@/engine/tauriBridge';

/**
 * 카드 공용 조각 + 어휘 번역(재설계 §3.4) — UI 스트링만 작가 언어로, 엔진 DTO는 무변경.
 * hard→세계와 충돌 · unchecked→미검증 · proposal→수리 제안 · RemoveAssert→이 전제 빼기 ·
 * NeedsRewrite→작가 손 필요(보류) · confidence→확신 N%.
 */

export const SEVERITY_LABEL: Record<string, string> = {
  critical: '치명',
  major: '중대',
  minor: '경미',
  info: '참고',
};

export const STATUS_LABEL: Record<string, string> = {
  pending: '대기',
  accepted: '수락됨',
  rejected: '뺌',
};

export const STOP_REASON_LABEL: Record<AutowriteStop, string> = {
  HardViolation: '봉합 불가 — 봉인/정합 충돌(폐기)',
  NeedsRewriteProposal: '작가 재작성 필요',
  RepairPreviewNonZero: '봉합하려면 모델 의미를 버려야 함(전제 제거)',
  EmptyExtraction: '추출 0 — 검증 불가(작가 확인)',
  ExtractorLowConfidence: '추출 신뢰 낮음 — 검증 불확실(작가 확인)',
  CharacterOsForbiddenReveal: '캐릭터 OS 금지 폭로 — 폐기',
  CharacterOsRevealBudgetZero: '캐릭터 OS 폭로 예산 0 — 폐기',
  CharacterOsRevealBudgetExceeded: '캐릭터 OS 폭로 예산 초과 — 폐기',
};

export function confidencePct(c: number): string {
  return `확신 ${Math.round(c * 100)}%`;
}

export function CanonRefChips({ refs }: { refs: string[] }) {
  if (refs.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {refs.map((r, i) => (
        <span key={i} className="ref-chip">
          {r}
        </span>
      ))}
    </div>
  );
}

/** 세계와 충돌(위반) 한 건 — 엔진 어휘(kind·id)는 모노 소자로 강등. */
export function ViolationItem({ v }: { v: EngineViolationDTO }) {
  return (
    <li className="rounded border border-line bg-bg/60 p-2">
      <div className="mb-1 flex items-center gap-1.5">
        <span className={`mono text-[10px] ${v.severity === 'critical' ? 'text-red-300' : v.severity === 'major' ? 'text-amber-300' : 'text-fainter'}`}>
          {SEVERITY_LABEL[v.severity] ?? v.severity}
        </span>
        <span className="mono text-[10px] text-fainter">{v.kind}</span>
        <span className="mono text-[10px] text-fainter">{confidencePct(v.confidence)}</span>
      </div>
      <p className="text-[11px] leading-4 text-dim">{v.evidence}</p>
      <CanonRefChips refs={v.canon_refs} />
    </li>
  );
}

/**
 * 수리 제안 한 건 — ledger 경계 그대로(불변원칙 1):
 * 이 전제 빼기(기계적 파장)만 1클릭, 작가 손 필요(드라마/구조)는 보류 메모.
 */
export function ProposalItem({
  proposal,
  mine,
  applied,
  onApply,
}: {
  proposal: EngineProposalDTO;
  mine: boolean;
  applied: boolean;
  onApply: (claim: string) => void;
}) {
  const { patch } = proposal;
  return (
    <li className="rounded border border-line bg-bg/60 p-2">
      <p className="text-[11px] leading-4 text-dim">{proposal.evidence}</p>
      {patch.type === 'RemoveAssert' ? (
        <button
          type="button"
          disabled={!mine || applied}
          onClick={() => onApply(patch.claim)}
          className="mt-2 w-full rounded border border-line px-2 py-1 text-left text-[11px] text-dim transition hover:border-green-400 hover:text-green-300 disabled:cursor-not-allowed disabled:border-line disabled:text-fainter"
        >
          {applied
            ? '뺐다 — 다음 검증에서 확인된다'
            : mine
              ? `이 전제 빼기 «${patch.claim}»`
              : `다른 비트(${proposal.beat})의 제안 — 그 비트에서`}
        </button>
      ) : (
        <p className="mt-1 text-[10px] leading-4 text-amber-300/80">작가 손 필요(보류) — {patch.note}</p>
      )}
    </li>
  );
}

export function CharacterOsChips({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  return <p className="mb-2 text-[11px] leading-5 text-star/80">✦ 캐릭터 OS 근거: {names.join(' · ')}</p>;
}

export function CardError({ error }: { error?: string }) {
  if (!error) return null;
  return <p className="mb-2 text-[11px] leading-5 text-red-300">{error}</p>;
}

export function EmptyHint({ children }: { children: React.ReactNode }) {
  return <div className="rounded-md border border-dashed border-line-2 bg-bg-2/50 p-2 text-[11px] text-faint">{children}</div>;
}
